import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ErrorSummary, type SummaryIssue } from '../../job-roles/components/ErrorSummary';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { bdErrorMessage, errorCode, isForbidden, isVersionConflict } from '../bdErrors';
import { DEDUCTION_CATEGORY_LABELS, type DeductionCategoryCode } from '../categories';
import { declaredTaxFormToInput, declaredTaxIssues, emptyDeclaredTaxForm, hasDeclaredTaxIssues, inlineCreateIssues, type DeclaredTaxFormState } from '../declaredTaxProfile';
import { DEDUCTION_FORM_WARNINGS, GARNISHMENT_RULES_NOTE, NO_CALCULATION_NOTE } from '../copy';
import {
  activationBlocker, applyDeductionCatalogType, definitionToForm, emptyDefinitionForm, formToDefinitionInput, isGarnishmentCategory, isPercentType, suggestDeductionShortCode,
  type DefinitionFormState,
} from '../definitionForm';
import { deductionsGateway } from '../index';
import { loadAllPlans } from '../loaders';
import {
  ARREARS_BEHAVIORS, BdErrorCode, CATCH_UP_BEHAVIORS, DEDUCTION_CALCULATION_TYPES, DEDUCTION_CLASSIFICATIONS, PAYROLL_FREQUENCIES, STOP_RULES, arrearsBehaviorLabels,
  catchUpBehaviorLabels, deductionCalculationTypeLabels, deductionClassificationLabels, payrollFrequencyLabels, stopRuleLabels,
  type BenefitPlan, type DeductionCatalogItem, type DeductionDefinition, type LimitRule, type TaxProfile,
} from '../types';
import { DeclaredTaxFields } from './DeclaredTaxFields';
import { RenderSlot } from './RenderSlot';
import { StatusBadge } from './ListControls';
import { CheckboxInput, RelatedSelect, SelectInput, TextAreaInput, TextInput } from './fields';
import '../../job-roles/job-roles.css';
import '../benefits-deductions.css';

export type DefinitionDialogMode = 'create' | 'edit' | 'view';

export interface DefinitionFormDialogProps {
  mode: DefinitionDialogMode;
  /** The page's category (fixed: a deduction is added to the page it is added from). */
  category: DeductionCategoryCode;
  /** Create from Employee Profile Setup: lets the person choose the category. Omitted on a category page, where the category is fixed. */
  categoryChoices?: DeductionCategoryCode[];
  /** Create from Employee Profile Setup: the tax treatment is required, and declared here as "needs review" (contract 4.2.1). Create only. */
  requireDeclaredTax?: boolean;
  /** Create: start from this catalog type. Null or omitted starts a custom deduction. */
  catalogType?: DeductionCatalogItem | null;
  definitionId?: string;
  onClose: () => void;
  onSaved: (definition: DeductionDefinition, mode: 'create' | 'edit') => void;
  onForbidden: () => void;
  /** The canonical "Add a benefit plan" dialog, supplied by the page so the person can create the plan in place without losing this form. */
  renderCreateBenefit?: (props: { onCreated: (plan: BenefitPlan) => void; onClose: () => void }) => ReactNode;
}

type Load = { kind: 'loading' } | { kind: 'ready'; definition: DeductionDefinition | null } | { kind: 'error'; message: string };

const idOf = (field: string) => `bd-def-${field}`;

/**
 * Add, edit or view a deduction definition with every field of the contract (section 4.2), plain-language help, and the required warnings. Custom
 * deductions are not automatically pre-tax; a deduction whose tax treatment is not approved is saved inactive and says why; a percentage or other
 * non-fixed calculation can be saved but cannot be activated yet; a garnishment or levy is involuntary, uses the approved formula, needs rule review and
 * carries no limit or priority logic here.
 */
export function DefinitionFormDialog({ mode, category, categoryChoices, requireDeclaredTax, catalogType, definitionId, onClose, onSaved, onForbidden, renderCreateBenefit }: DefinitionFormDialogProps) {
  const readOnlyView = mode === 'view';
  const [load, setLoad] = useState<Load>(mode === 'create' ? { kind: 'ready', definition: null } : { kind: 'loading' });
  const [form, setForm] = useState<DefinitionFormState>(() => {
    const base = emptyDefinitionForm(category);
    return catalogType ? { ...applyDeductionCatalogType(base, catalogType), shortCode: suggestDeductionShortCode(catalogType.name) } : base;
  });
  const [shortCodeTouched, setShortCodeTouched] = useState(false);
  const needsDeclared = requireDeclaredTax === true && mode === 'create';
  const [declared, setDeclared] = useState<DeclaredTaxFormState>(emptyDeclaredTaxForm);
  const [declaredAttempted, setDeclaredAttempted] = useState(false);
  const [profiles, setProfiles] = useState<TaxProfile[]>([]);
  const [rules, setRules] = useState<LimitRule[]>([]);
  const [plans, setPlans] = useState<BenefitPlan[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState<SummaryIssue[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [busy, setBusy] = useState(false);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const focusPlanAfter = useRef(false);
  const summaryRef = useRef<HTMLDivElement>(null);
  const planSelectRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (mode === 'create' || !definitionId) return;
    let cancelled = false;
    deductionsGateway.getDefinition(definitionId)
      .then((definition) => { if (!cancelled) { setForm(definitionToForm(definition)); setShortCodeTouched(true); setLoad({ kind: 'ready', definition }); } })
      .catch((err: unknown) => { if (!cancelled) { if (isForbidden(err)) onForbidden(); setLoad({ kind: 'error', message: bdErrorMessage(err, 'Could not load this deduction.', 'deduction') }); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, definitionId, reloadNonce]);

  useEffect(() => {
    let cancelled = false;
    deductionsGateway.listTaxProfiles().then((p) => { if (!cancelled) setProfiles(p); }).catch(() => undefined);
    deductionsGateway.listLimitRules({ includeUnapproved: true }).then((r) => { if (!cancelled) setRules(r); }).catch(() => undefined);
    loadAllPlans().then((p) => { if (!cancelled) setPlans(p); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  // Focus the plan select once the nested dialog has closed and returned focus to its opener.
  useEffect(() => {
    if (!focusPlanAfter.current || creatingPlan) return;
    planSelectRef.current?.focus();
    focusPlanAfter.current = false;
  }, [creatingPlan]);

  const definition = load.kind === 'ready' ? load.definition : null;
  const disabled = readOnlyView || busy;
  const set = <K extends keyof DefinitionFormState>(key: K, value: DefinitionFormState[K]) => setForm((f) => ({ ...f, [key]: value }));
  const garnishment = isGarnishmentCategory(form.categoryCode);
  const percent = isPercentType(form.calculationType);
  const blocker = activationBlocker(form);

  function changeName(value: string) {
    setForm((f) => ({ ...f, name: value, ...(shortCodeTouched ? {} : { shortCode: suggestDeductionShortCode(value) }) }));
  }

  function showSummary(list: SummaryIssue[]) {
    setSummary(list);
    setTimeout(() => summaryRef.current?.focus(), 0);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || readOnlyView) return;
    setFormError(null);
    setConflict(false);
    const { input, issues: formIssues } = formToDefinitionInput(form);
    const issues = needsDeclared ? [...formIssues, ...inlineCreateIssues(form)] : formIssues;
    const declaredProblem = needsDeclared ? declaredTaxIssues(declared, true) : null;
    const declaredBlocked = declaredProblem !== null && hasDeclaredTaxIssues(declaredProblem);
    if (issues.length > 0 || declaredBlocked) {
      const byField: Record<string, string> = {};
      for (const issue of issues) if (!byField[issue.field]) byField[issue.field] = issue.message;
      setErrors(byField);
      setDeclaredAttempted(true);
      const list = Object.entries(byField).map(([field, message]) => ({ key: idOf(field), message }));
      if (declaredBlocked) list.push({ key: 'bd-def-declared', message: declaredProblem.treatment ?? declaredProblem.cells ?? 'Choose the tax treatment.' });
      showSummary(list);
      return;
    }
    setErrors({});
    setSummary([]);
    setBusy(true);
    try {
      const saved = mode === 'create'
        ? await deductionsGateway.createDefinition(needsDeclared ? { ...input, declaredTaxProfile: declaredTaxFormToInput(declared) } : input)
        : await deductionsGateway.updateDefinition(definitionId!, { ...input, expectedVersion: definition!.concurrencyVersion });
      onSaved(saved, mode === 'create' ? 'create' : 'edit');
    } catch (err) {
      if (isForbidden(err)) onForbidden();
      if (isVersionConflict(err)) setConflict(true);
      else if (errorCode(err) === BdErrorCode.DuplicateName) setErrors({ name: bdErrorMessage(err, '', 'deduction') });
      else if (errorCode(err) === BdErrorCode.DuplicateCode) setErrors({ shortCode: bdErrorMessage(err, '', 'deduction') });
      else setFormError(bdErrorMessage(err, mode === 'create' ? 'Could not add this deduction.' : 'Could not save your changes.', 'deduction'));
      setBusy(false);
    }
  }

  function handlePlanCreated(plan: BenefitPlan) {
    setPlans((list) => (list.some((p) => p.id === plan.id) ? list : [...list, plan]));
    set('associatedBenefitCodeId', plan.id);
    focusPlanAfter.current = true;
    setCreatingPlan(false);
  }

  const title = mode === 'create'
    ? (catalogType && catalogType.code === form.typeCode && form.typeCode !== '' ? `Add ${catalogType.name}` : 'Add Custom Deduction')
    : mode === 'edit' ? `Edit ${definition?.name ?? 'deduction'}` : `View ${definition?.name ?? 'deduction'}`;

  const classificationOptions = DEDUCTION_CLASSIFICATIONS.filter((c) => c !== 'Statutory' || form.classification === 'Statutory').map((c) => ({ value: c, label: deductionClassificationLabels[c] }));
  const profileOptions = [{ value: '', label: 'None yet (tax review required)' }, ...profiles.map((p) => ({ value: p.code, label: p.name }))];
  const ruleOptions = [
    { value: '', label: 'None' },
    ...rules.map((r) => ({ value: r.code, label: `${r.name}${r.status === 'Approved' ? '' : ` (${r.status === 'Draft' ? 'draft, no amount yet' : r.status.toLowerCase()})`}` })),
  ];
  const planOptions = [
    { value: '', label: 'None' },
    ...plans.map((p) => ({ value: p.id, label: p.name })),
    ...(form.associatedBenefitCodeId && !plans.some((p) => p.id === form.associatedBenefitCodeId) ? [{ value: form.associatedBenefitCodeId, label: 'Linked benefit plan' }] : []),
  ];
  const amountLabel = percent ? 'Percent' : form.calculationType === 'RemainingBalanceUpToPerPayrollAmount' ? 'Amount each payroll' : 'Amount';

  return (
    <>
      <ModalDialog
        title={title}
        size="lg"
        busy={busy}
        onClose={onClose}
        footer={(
          <>
            <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>{readOnlyView ? 'Close' : 'Cancel'}</button>
            {!readOnlyView && load.kind === 'ready' && (
              <button type="submit" form="bd-def-form" className="jr-btn jr-btn--primary" disabled={busy}>
                {busy ? 'Saving…' : mode === 'create' ? 'Add deduction' : 'Save changes'}
              </button>
            )}
          </>
        )}
      >
        {load.kind === 'loading' && <p className="muted" role="status">Loading deduction{'…'}</p>}
        {load.kind === 'error' && (
          <div role="alert">
            <p className="jr-error">{load.message}</p>
            <button type="button" className="jr-btn jr-btn--secondary" onClick={() => { setLoad({ kind: 'loading' }); setReloadNonce((n) => n + 1); }}>Try again</button>
          </div>
        )}
        {load.kind === 'ready' && (
          <form id="bd-def-form" className="bd-form" onSubmit={(e) => void submit(e)} noValidate>
            <div className="bd-warnings" role="note" aria-label="Before you continue">
              <ul>{DEDUCTION_FORM_WARNINGS.map((w) => <li key={w}>{w}</li>)}</ul>
            </div>
            {garnishment && <p className="bd-notice bd-notice--warn" role="note">{GARNISHMENT_RULES_NOTE} Rule review required: a garnishment or levy cannot be activated from here.</p>}
            <ErrorSummary ref={summaryRef} issues={summary} onSelect={(key) => document.getElementById(key)?.focus()} />
            {formError && <p className="jr-error jr-error--block" role="alert">{formError}</p>}
            {conflict && (
              <div className="jr-conflict" role="alert">
                <p>This deduction was changed by someone else after you opened it, so your changes were not saved. Reload the latest version, then make your changes again.</p>
                <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => { setConflict(false); setLoad({ kind: 'loading' }); setReloadNonce((n) => n + 1); }}>Reload latest</button>
              </div>
            )}
            {definition && (
              <div className="bd-status-line">
                <StatusBadge isActive={definition.isActive} configurationStatus={definition.configurationStatus} />
              </div>
            )}
            {definition && definition.configurationIssues.length > 0 && (
              <div className="bd-notice bd-notice--warn" role="status">
                <p><strong>What needs attention</strong></p>
                <ul className="bd-list">{definition.configurationIssues.map((i) => <li key={i}>{i}</li>)}</ul>
              </div>
            )}
            {percent && !garnishment && <p className="bd-notice bd-notice--warn" role="status">{NO_CALCULATION_NOTE} You can save this definition, but it stays inactive.</p>}

            <fieldset className="bd-fieldset" disabled={disabled}>
              <legend>About this deduction</legend>
              <div className="jr-grid">
                <SelectInput
                  id="bd-def-categoryCode" label="Category" required value={form.categoryCode}
                  onChange={(v) => { if (categoryChoices) set('categoryCode', v as DeductionCategoryCode); }} disabled={!categoryChoices || mode !== 'create'}
                  options={(categoryChoices ?? [form.categoryCode]).map((c) => ({ value: c, label: DEDUCTION_CATEGORY_LABELS[c] }))} error={errors.categoryCode}
                  help={categoryChoices ? 'Pick the category that fits this deduction.' : 'A deduction is added to the category page you are on.'}
                />
                <TextInput id="bd-def-name" label="Name" required value={form.name} onChange={changeName} error={errors.name} maxLength={120} autoFocus />
                <TextInput
                  id="bd-def-shortCode" label="Short code" required value={form.shortCode} onChange={(v) => { setShortCodeTouched(true); set('shortCode', v); }} error={errors.shortCode}
                  hint="2 to 30 letters, numbers, hyphens or underscores." maxLength={30} help="A short label used in lists and reports. It must be different from every other deduction's code."
                />
                <TextAreaInput id="bd-def-description" label="Description" required={needsDeclared || undefined} value={form.description} onChange={(v) => set('description', v)} maxLength={500} />
                <SelectInput
                  id="bd-def-classification" label="Classification" value={form.classification} onChange={(v) => set('classification', v as DefinitionFormState['classification'])} disabled={disabled || garnishment}
                  options={classificationOptions} error={errors.classification}
                  help="Voluntary: the employee chose it. Involuntary: an order or law requires it, such as a garnishment. Statutory taxes are calculated by the system and cannot be created here."
                />
                <SelectInput
                  id="bd-def-frequency" label="How often it is taken" value={form.frequency} onChange={(v) => set('frequency', v as DefinitionFormState['frequency'])}
                  options={PAYROLL_FREQUENCIES.map((f) => ({ value: f, label: payrollFrequencyLabels[f] }))}
                  help="How often the deduction is taken. Until payroll supports other schedules, a deduction follows the employee's pay schedule."
                />
                <TextInput id="bd-def-effectiveStartDate" label="Effective start date" required={needsDeclared || undefined} type="date" value={form.effectiveStartDate} onChange={(v) => set('effectiveStartDate', v)} help="The first day this deduction can be assigned. Leave it empty for no start date." />
                <TextInput id="bd-def-effectiveEndDate" label="Effective end date" type="date" value={form.effectiveEndDate} onChange={(v) => set('effectiveEndDate', v)} error={errors.effectiveEndDate} help="The last day this deduction can be assigned. Leave it empty if it does not end." />
              </div>
            </fieldset>

            {!garnishment && (
              <fieldset className="bd-fieldset" disabled={disabled}>
                <legend>Calculation, amounts and limits</legend>
                <div className="jr-grid">
                  <SelectInput
                    id="bd-def-calculationType" label="How the amount is worked out" value={form.calculationType} onChange={(v) => set('calculationType', v as DefinitionFormState['calculationType'])}
                    options={DEDUCTION_CALCULATION_TYPES.filter((t) => t !== 'ApprovedGarnishmentFormula').map((t) => ({ value: t, label: deductionCalculationTypeLabels[t] }))} error={errors.calculationType}
                    hint="Payroll honors only fixed amounts today. Other types can be saved but cannot be activated yet."
                    help="A fixed amount takes the same dollars each time. Percentage and other types need payroll support that is not available yet."
                  />
                  <TextInput id="bd-def-defaultAmount" label={amountLabel} value={form.defaultAmount} onChange={(v) => set('defaultAmount', v)} error={errors.defaultAmount} inputMode="decimal" hint={percent ? 'From 0 to 100. Calculated at payroll.' : 'Dollars, from 0 to 1,000,000. An employee can have their own amount when you assign it.'} />
                  <TextInput id="bd-def-defaultCompanyContribution" label="Employer contribution (optional)" value={form.defaultCompanyContribution} onChange={(v) => set('defaultCompanyContribution', v)} error={errors.defaultCompanyContribution} inputMode="decimal" hint="Dollars the company adds, if any." />
                  <TextInput id="bd-def-annualLimit" label="Annual limit (optional)" value={form.annualLimit} onChange={(v) => set('annualLimit', v)} error={errors.annualLimit} inputMode="decimal" help="The most that can be taken in a year. This is your own limit; statutory limits come from approved limit rules." />
                  <TextInput id="bd-def-perPayPeriodLimit" label="Limit each pay period (optional)" value={form.perPayPeriodLimit} onChange={(v) => set('perPayPeriodLimit', v)} error={errors.perPayPeriodLimit} inputMode="decimal" />
                  <TextInput id="bd-def-goalBalance" label="Goal balance (optional)" value={form.goalBalance} onChange={(v) => set('goalBalance', v)} error={errors.goalBalance} inputMode="decimal" help="The total to reach, for example the amount of a loan. Used with the stop rule below." />
                  <TextInput
                    id="bd-def-priority" label="Priority (optional)" value={form.priority} onChange={(v) => set('priority', v)} error={errors.priority} inputMode="numeric" hint="1 to 99, lower comes first."
                    help="Which deduction is taken first when pay is not enough. Phase 1 only stores this number: nothing uses it yet."
                  />
                  <SelectInput id="bd-def-arrearsBehavior" label="If pay is not enough" value={form.arrearsBehavior} onChange={(v) => set('arrearsBehavior', v as DefinitionFormState['arrearsBehavior'])} options={ARREARS_BEHAVIORS.map((b) => ({ value: b, label: arrearsBehaviorLabels[b] }))} help="What should happen to a deduction that could not be taken in full. Phase 1 stores your choice; payroll does not act on it yet." />
                  <SelectInput id="bd-def-catchUpBehavior" label="Catch-up" value={form.catchUpBehavior} onChange={(v) => set('catchUpBehavior', v as DefinitionFormState['catchUpBehavior'])} options={CATCH_UP_BEHAVIORS.map((b) => ({ value: b, label: catchUpBehaviorLabels[b] }))} />
                  <SelectInput id="bd-def-stopRule" label="When it stops" value={form.stopRule} onChange={(v) => set('stopRule', v as DefinitionFormState['stopRule'])} options={STOP_RULES.map((b) => ({ value: b, label: stopRuleLabels[b] }))} error={errors.stopRule} />
                </div>
              </fieldset>
            )}

            <fieldset className="bd-fieldset" disabled={disabled}>
              <legend>Payroll setup, tax and links</legend>
              <div className="jr-grid">
                {needsDeclared && <DeclaredTaxFields state={declared} onChange={setDeclared} issues={declaredTaxIssues(declared, true)} showIssues={declaredAttempted} disabled={disabled} idPrefix="bd-def-declared" />}
                <SelectInput
                  id="bd-def-taxProfileCode" label="Tax profile" value={form.taxProfileCode} onChange={(v) => set('taxProfileCode', v)} options={profileOptions} error={errors.taxProfileCode}
                  hint={profiles.length === 0 ? 'No tax profile is approved for payroll yet. Tax profiles are managed by Escochex, not by your company. Until one is approved, this deduction stays inactive.' : 'Tax profiles are managed by Escochex. Only profiles approved for payroll can be chosen.'}
                  help="A tax profile says how the deduction affects taxable wages. Custom deductions are not automatically pre-tax. Tax treatment must be approved before this deduction can be used in payroll."
                />
                <SelectInput
                  id="bd-def-limitRuleCode" label="Limit rule (optional)" value={form.limitRuleCode} onChange={(v) => set('limitRuleCode', v)} options={ruleOptions} error={errors.limitRuleCode}
                  hint="Limit rules are read only and versioned. A draft rule has no amount, so nothing is enforced yet." help="A statutory or plan limit that applies to this deduction, such as a yearly contribution limit."
                />
                <RelatedSelect
                  ref={planSelectRef} id="bd-def-associatedBenefitCodeId" label="Linked benefit plan (optional)" value={form.associatedBenefitCodeId} onChange={(v) => set('associatedBenefitCodeId', v)}
                  options={planOptions} help="The benefit plan this deduction pays for. Linking it does not enroll anyone."
                  onAdd={renderCreateBenefit && !disabled ? () => setCreatingPlan(true) : undefined} addLabel="Add a benefit plan"
                  noAddNote="Ask an administrator to add a benefit plan if the one you need is missing."
                />
                <TextInput id="bd-def-remittanceRecipientName" label="Who receives the money (optional)" value={form.remittanceRecipientName} onChange={(v) => set('remittanceRecipientName', v)} maxLength={120} help="The name of the organization the deducted money is sent to, such as a union local. EPN only records it: nothing is paid from here." />
                <TextInput id="bd-def-employeeVisibleLabel" label="Label employees see (optional)" value={form.employeeVisibleLabel} onChange={(v) => set('employeeVisibleLabel', v)} maxLength={80} help="The name shown on an employee's view of the deduction. Leave it empty to use the deduction's name." />
                <TextAreaInput id="bd-def-internalNotes" label="Internal notes" value={form.internalNotes} onChange={(v) => set('internalNotes', v)} maxLength={1000} hint="Only administrators see this. Do not enter court case numbers, account numbers or other personal data." />
                <CheckboxInput id="bd-def-requiresEmployeeAuthorization" label="Needs the employee's written authorization" checked={form.requiresEmployeeAuthorization} onChange={(v) => set('requiresEmployeeAuthorization', v)} help="Check this when you must hold the employee's signed approval before the deduction starts. EPN does not store the document." />
                <CheckboxInput
                  id="bd-def-isActive" label="Activate this deduction" checked={form.isActive && blocker === null} onChange={(v) => set('isActive', v)} disabled={disabled || blocker !== null}
                  hint={blocker ?? 'An active deduction can be assigned to employees.'}
                />
              </div>
            </fieldset>
          </form>
        )}
      </ModalDialog>
      {creatingPlan && renderCreateBenefit && (
        <RenderSlot render={renderCreateBenefit} props={{ onCreated: handlePlanCreated, onClose: () => setCreatingPlan(false) }} />
      )}
    </>
  );
}

